
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export const downloadBlob = (
  blob: Blob,
  fileName: string
) => {
  try {
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error("Download failed:", error);
    return false;
  }
};

export const downloadReceipt = async (receipt: HTMLElement, fileName: string) => {
  const { width } = receipt.getBoundingClientRect();
  if (!width || !receipt.scrollHeight) {
    throw new Error("Receipt content is not available for download");
  }

  const canvas = await html2canvas(receipt, {
    scale: 4,
    useCORS: true,
    backgroundColor: "#ffffff",
    onclone: (clonedDocument: Document) => {
      clonedDocument.querySelectorAll<HTMLElement>("[data-pdf-kct-label]").forEach((label) => {
        label.style.setProperty("display", "flex", "important");
        label.style.setProperty("align-items", "center", "important");
        label.style.setProperty("justify-content", "center", "important");
        label.style.setProperty("line-height", "10px", "important");
        label.style.setProperty("padding", "0", "important");
        label.style.setProperty("margin", "0", "important");
        label.style.setProperty("transform", "translateY(-2.5px)", "important");
      });

      clonedDocument.querySelectorAll<HTMLElement>("[data-pdf-token-value]").forEach((tokenValue) => {
        const parent = tokenValue.parentElement as HTMLElement | null;
        if (!parent) return;

        const style = clonedDocument.defaultView?.getComputedStyle(tokenValue);
        const fontSize = Number.parseFloat(style?.fontSize ?? "12") || 12;
        const fontWeight = style?.fontWeight || "600";
        const fontFamily = style?.fontFamily || "Aptos, Arial, sans-serif";
        const textColor = style?.color || "#222222";
        const width = parent.getBoundingClientRect().width || parent.offsetWidth || 80;
        const height = parent.getBoundingClientRect().height || parent.offsetHeight || 32;

        const replacementCanvas = clonedDocument.createElement("canvas");
        const ratio = 2;
        replacementCanvas.width = Math.ceil(width * ratio);
        replacementCanvas.height = Math.ceil(height * ratio);

        const context = replacementCanvas.getContext("2d");
        if (!context) return;

        context.clearRect(0, 0, replacementCanvas.width, replacementCanvas.height);
        context.textAlign = "center";
        context.textBaseline = "middle";
        context.fillStyle = textColor;
        context.font = `${fontWeight} ${fontSize * ratio}px ${fontFamily}`;
        context.fillText(tokenValue.textContent?.trim() || "", replacementCanvas.width / 2, replacementCanvas.height / 2);

        replacementCanvas.style.width = `${width}px`;
        replacementCanvas.style.height = `${height}px`;
        replacementCanvas.style.display = "block";
        replacementCanvas.style.margin = "0";
        replacementCanvas.style.verticalAlign = "middle";
        replacementCanvas.style.background = "transparent";

        tokenValue.replaceWith(replacementCanvas);
      });
    },
  });

  const margin = 3;
  const pixelsToMillimeters = 25.4 / 96;
  const contentWidth = width * pixelsToMillimeters;
  const contentHeight = contentWidth * (canvas.height / canvas.width);
  const pdf = new jsPDF({
    unit: "mm",
    format: [contentWidth + margin * 2, contentHeight + margin * 2],
    orientation: "portrait",
  });
  const imageData = canvas.toDataURL("image/jpeg", 0.98);
  pdf.addImage(imageData, "JPEG", margin, margin, contentWidth, contentHeight);
  const pdfBlob = pdf.output("blob");

  if (!downloadBlob(pdfBlob, fileName)) {
    throw new Error("Unable to save the receipt PDF");
  }
};

export const downloadBase64File = (
  base64String: string,
  fileName: string,
  mimeType: string = "application/pdf"
) => {
  try {
    // Remove data URL prefix if present (e.g., "data:application/pdf;base64,")
    const base64Data = base64String.includes(",")
      ? base64String.split(",")[1]
      : base64String;

    // Convert base64 to blob
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error("Download failed:", error);
    return false;
  }
};

/**
 * Download file from URL
 */
export const downloadFileFromUrl = async (
  url: string,
  fileName: string
) => {
  try {
    // Fetch the file
    const response = await fetch(url);
    const blob = await response.blob();

    // Create download link
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
    
    return true;
  } catch (error) {
    console.error("Download failed:", error);
    return false;
  }
};

/**
 * Download file from API response
 * Handles both base64 and URL formats
 */
export const downloadInvoiceFile = (
  fileData: Blob | string,
  invoiceNumber: string,
  fileType: "invoice" | "receipt" = "invoice"
) => {
  const fileName = `${fileType}_${invoiceNumber}_${Date.now()}.pdf`;

  // Check if it's a Blob
  if (fileData instanceof Blob) {
    return downloadBlob(fileData, fileName);
  }

  // Check if it's a URL
  if (typeof fileData === "string" && (fileData.startsWith("http://") || fileData.startsWith("https://"))) {
    return downloadFileFromUrl(fileData, fileName);
  }
  
  // Otherwise treat as base64
  if (typeof fileData === "string") {
    return downloadBase64File(fileData, fileName);
  }

  console.error("Unknown file data format");
  return false;
};

/**
 * Generate filename with timestamp
 */
export const generateFileName = (
  prefix: string,
  extension: string = "pdf"
): string => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
  return `${prefix}_${timestamp}.${extension}`;
};